import React from 'react';
import { css } from '@emotion/react';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { DateSelector } from '../../../../components/mui/dateSelector';
import { OutlineButton } from '../../../../components/outlineButton';
import { usePrint } from '../../../../hooks/usePrint';
import { useFetchGasAnalytics } from '../../../../hooks/api/traversal/useFetchGasAnalytics';
import { useFetchGasDataSets } from '../../../../hooks/api/bDevice/useFetchGasDataSets';
import { useFetchResidentNotifications } from '../../../../hooks/api/notification/useFetchResidentNotifications';
import { defaultData } from './defaultData';
import { AnalyticsUpperRightContents } from './analyticsUpperRightContents';
import { DefecationAnalyticsCalendar } from './defecationAnalyticsCalendar';
import { DefecationRatioPieChart } from './defecationRatioPieChart';
import { DefecationTimeFromNotification } from './defecationTimeFromNotification';
import { DefecationBarChart } from './defecationBarChart';
import { DefecationHardnessTypeBarChart } from './defecationHardnessTypeBarChart';
import { DefecationDrinkEatRelationChart } from './defecationDrinkEatRelationChart';

interface Props {
  nursingHomeId: string;
  residentId: string;
}

const PrintHidden = css`
  @media print {
    display: none;
  }
`;

const PrintPageBreak = css`
  @media print {
    break-before: page;
    page-break-before: always;
  }
`;

const Container = css`
  padding: 16px;
`;

const DatePickerContainer = css`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0 32px 16px;
`;

const DatePickerText = css`
  font-size: 16px;
  font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
`;

const UpperContainer = css`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 24px;
`;

const CalendarStyle = css`
  flex: 5 1 720px;
`;

const RightStyle = css`
  display: flex;
  flex-wrap: wrap;
  flex: 1 1 300px;
  gap: 0 32px;
`;

const LowerContainer = css`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const LowerItemStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 16px;
`;

export const svgIcons: Record<string, string> = {
  korokoro: '/images/residents/defecationAnalysis/korokoro.svg',
  hard: '/images/residents/defecationAnalysis/hard.svg',
  littleHard: '/images/residents/defecationAnalysis/littleHard.svg',
  middle: '/images/residents/defecationAnalysis/middle.svg',
  soft: '/images/residents/defecationAnalysis/soft.svg',
  littleSoft: '/images/residents/defecationAnalysis/littleSoft.svg',
  water: '/images/residents/defecationAnalysis/water.svg',
  bit: '/images/residents/defecationAnalysis/bit.svg',
  leakOut: '/images/residents/defecationAnalysis/leakOutFeces.svg',
};

export const ResidentDetailDefecationAnalytics: React.FC<Props> = React.memo(({ nursingHomeId, residentId }) => {
  const initialDate = dayjs().local().startOf('day');
  const [startDate, setStartDate] = React.useState<Dayjs>(initialDate.subtract(29, 'day').startOf('day').utc());
  const [endDate, setEndDate] = React.useState<Dayjs>(initialDate.endOf('day').utc());
  const [isPrinting, setIsPrinting] = React.useState(false);
  const onPrinting = React.useCallback((isPrinting: boolean) => {
    setIsPrinting(isPrinting);
  }, []);

  const { printRef, handlePrint } = usePrint(onPrinting);
  const { gasAnalytics, fetchGasAnalytics } = useFetchGasAnalytics(
    true,
    +nursingHomeId,
    +residentId,
    startDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
    endDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]')
  );
  const { residentNotifications, fetchResidentNotifications } = useFetchResidentNotifications(
    +residentId,
    true,
    startDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
    endDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
    1
  );
  const { gasDataSets, fetchGasDataSets } = useFetchGasDataSets(+residentId, true);

  const analyticsData = gasAnalytics ?? defaultData;

  const handleChangeDate = (newDate: Dayjs) => {
    const startDate = newDate.local().subtract(29, 'day').startOf('day').utc();
    const endDate = newDate.local().endOf('day').utc();
    setStartDate(startDate);
    setEndDate(endDate);
    fetchGasAnalytics(
      +nursingHomeId,
      +residentId,
      startDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      endDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]')
    );
    fetchResidentNotifications(
      startDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      endDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
      1
    );
    fetchGasDataSets();
  };

  const PrintButton = ({ disable }: { disable: boolean }) => {
    return (
      <OutlineButton disable={disable} onClick={handlePrint}>
        <span>{t('Print', '印刷')}</span>
      </OutlineButton>
    );
  };

  return (
    <div css={Container} ref={printRef} className='print-container'>
      <div css={DatePickerContainer}>
        <span css={DatePickerText}>{startDate.local().format('YYYY/MM/DD')}</span>
        <span css={DatePickerText}>~</span>
        <DateSelector
          newValue={endDate}
          minDate={initialDate.subtract(3, 'years')}
          maxDate={initialDate}
          onChange={handleChangeDate}
        />
        <div css={PrintHidden}>
          <PrintButton disable={false} />
        </div>
      </div>
      <div css={UpperContainer}>
        <div css={CalendarStyle}>
          <DefecationAnalyticsCalendar
            startDate={startDate.local().format('YYYY-MM-DD HH:mm')}
            endDate={endDate.local().format('YYYY-MM-DD')}
            careRecords={analyticsData.careRecords}
            notifications={residentNotifications}
            gasDataSets={gasDataSets}
          />
        </div>
        <div css={RightStyle}>
          <AnalyticsUpperRightContents data={analyticsData.results} />
        </div>
      </div>
      <div css={LowerContainer}>
        <DefecationDrinkEatRelationChart
          startDate={startDate.local().format('YYYY-MM-DD HH:mm')}
          careRecords={analyticsData.careRecords}
        />
      </div>
      <div css={[LowerContainer, PrintPageBreak]}>
        <div css={LowerItemStyle}>
          <DefecationRatioPieChart countFecesByTerm={analyticsData.results.countFecesByTerm} />
          <DefecationTimeFromNotification termNotification={analyticsData.results.termNotification} />
          <DefecationHardnessTypeBarChart data={analyticsData.results.countFecesTypes} />
        </div>
        <div css={[LowerItemStyle, PrintPageBreak]}>
          <DefecationBarChart dailyLaxative={analyticsData.results.dailyLaxative.dailyLaxative} />
        </div>
      </div>
    </div>
  );
});
